import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
  HttpQueryParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { PublicProductDetailsResponse } from '../responses/public-product-details.response';
import { PublicProductCompositeCollectionItemResponse } from '../responses/public-product-composite-collection-item.response';
import { Nillable, valueOrDefault } from '@web/utils';
import { PublicAvailableProductResponse } from '../responses/public-available-product.response';
import { PublicProductVisibilityResponse } from '../responses/public-product-visibility.response';
import { PublicProductAuthorizeRequest } from '../requests/public-product-authorize.request';
import { PublicProductAuthorizeResponse } from '../responses/public-product-authorize.response';
import { PublicProductResponse } from '../responses/public-product.response';

@Injectable({
  providedIn: 'root',
})
export class PublicProductApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchAvailableProductCollection(options?: {
    cacheable: boolean;
  }): Observable<CollectionResponse<PublicAvailableProductResponse>> {
    const path = 'product/available';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicAvailableProductResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsBySlug(
    slug: string,
    token?: Nillable<string>,
    cacheable = false,
  ): Observable<PublicProductDetailsResponse> {
    const path = `product/${HttpParams.Slug}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams({
          [HttpQueryParams.AccessToken]: token,
        })
        .params({
          [HttpParams.Slug]: slug,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(PublicProductDetailsResponse)),
      );
  }

  public fetchCompositeCollection(options: {
    request: CollectionRequest;
    cacheable?: Nillable<boolean>;
  }): Observable<
    CollectionResponse<PublicProductCompositeCollectionItemResponse>
  > {
    const { request, cacheable } = options;
    const path = `product/composite`;
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicProductCompositeCollectionItemResponse,
          ),
        ),
      );
  }

  public authorize(
    slug: string,
    access: PublicProductAuthorizeRequest,
  ): Observable<PublicProductAuthorizeResponse> {
    const path = `product/${HttpParams.Slug}/access-token`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      access,
      PublicProductAuthorizeRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicProductAuthorizeResponse,
          ),
        ),
      );
  }

  public checkVisibilityBySlug(
    slug: string,
    cacheable = false,
  ): Observable<PublicProductVisibilityResponse> {
    const path = `product/${HttpParams.Slug}/status`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicProductVisibilityResponse,
          ),
        ),
      );
  }

  public fetchProductsCollection(options: {
    request: CollectionRequest;
    cacheable?: Nillable<boolean>;
  }): Observable<CollectionResponse<PublicProductResponse>> {
    const path = 'product';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options.cacheable, false),
      )
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicProductResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
